/**
 * What has been done to one row.
 *
 * Paged the same way the comments beside it are, and marked stale by the
 * realtime layer rather than patched -- see `features/realtime/handlers/
 * database.ts`. Newest first, which is the endpoint's own order.
 *
 * **The entry carries the field as it was.** `fields_metadata` is a snapshot
 * taken when the change was made: the field's id and type, the properties that
 * decide how its values read -- a date's format, a number's decimals, a
 * duration's unit -- and, for the types that store references rather than
 * values, the table to resolve them through. That is what lets a change to a
 * field somebody has since turned into text still read as the date it was, and
 * what lets an option that has since been renamed still show the name it had.
 *
 * **So there is one rendering, not ten.** Upstream ships a component per field
 * type for this. Here the metadata *is* a field, and every type already knows
 * how to render its own value -- so a history value is put back into the shape
 * a cell of that type holds, and then read by the same `toHumanReadable` the
 * grid reads a cell with. Two types are left over and neither is a rendering
 * problem: a password stores whether one was set rather than what it was, and
 * the four reference types store ids that only the metadata can name.
 */
import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { Field, FieldTypeName, RowHistory } from '@/api/types'
import { dayHeading, type DayHeading } from '@/shared/lib/when'
import { FIELD_BEHAVIOR, behaviorOf } from '../fields/behavior'
import type { FieldContext } from '../fields/behavior/behavior'

export const HISTORY_PAGE_SIZE = 20

/**
 * A field as it was when the change was made.
 *
 * Not a `Field`, and deliberately kept open: it holds `id`, `type` and only
 * whichever of that type's properties decide how its values read. Every
 * renderer this feeds takes a field and reads the properties it needs, so the
 * snapshot passes for one where it matters and carries nothing where it does
 * not.
 */
export interface HistoryFieldMetadata {
  id: number
  type: string
  select_options?: Record<string, { id: number; value: string; color: string }>
  linked_rows?: Record<string, { value: string }>
  collaborators?: Record<string, { id: number; name: string }>
  [key: string]: unknown
}

/**
 * The three things the schema gives up on, narrowed.
 *
 * Generic over the entry so that the database-wide activity history -- whose
 * entries carry four more properties -- narrows the same three the same way,
 * and stays assignable to everything here that reads an entry.
 */
type NarrowedValues = {
  before: Record<string, unknown>
  after: Record<string, unknown>
  fields_metadata: Record<string, HistoryFieldMetadata>
}

export type HistoryEntryOf<T> = Omit<T, keyof NarrowedValues> & NarrowedValues

/** One entry in a row's own history. */
export type HistoryEntry = HistoryEntryOf<RowHistory>

export interface HistoryPage {
  count: number
  next: string | null
  previous: string | null
  results: HistoryEntry[]
}

/**
 * The six actions that leave an entry behind, and what each of them did.
 *
 * Six rather than the eight upstream names. `submit_form` and `edit_form_row`
 * have no history provider registered, so neither can ever reach this -- and
 * `update_row` does not exist as an action at all: editing one row is
 * `update_rows` with one row in it.
 */
const ACTION_PHRASE = {
  create_row: 'created',
  create_rows: 'created',
  update_rows: 'updated',
  delete_row: 'deleted',
  delete_rows: 'deleted',
  restore_from_trash: 'restored',
} as const

export type HistoryPhrase = (typeof ACTION_PHRASE)[keyof typeof ACTION_PHRASE]

/**
 * What an entry says happened, or null when nothing here recognises it.
 *
 * Null is a real answer rather than a fallback phrase: an entry naming an
 * action this build has never heard of is still a record of something, and
 * saying "updated this row" about it would be a guess. The panel names the
 * action itself instead.
 */
export function historyPhrase(actionType: string): HistoryPhrase | null {
  return ACTION_PHRASE[actionType as keyof typeof ACTION_PHRASE] ?? null
}

/**
 * How a history value relates to the value a cell of that type holds.
 *
 * `'same'` covers every type that stores its value rather than a reference to
 * one -- which is most of them, files included, since an attachment is stored
 * whole on both sides.
 *
 * The four reference kinds store ids. They have to: an option, a linked row and
 * a member can all be renamed after the change was made, and an entry that
 * quoted the name it had then would go on quoting it forever. The names in the
 * entry's metadata are the ones that were current when it was written, which is
 * the right answer for a record of what happened.
 *
 * `'password'` is neither. Both sides are a boolean saying whether one was set,
 * because the hash must not leave the server -- so there is nothing to diff and
 * the change is described instead.
 */
const HISTORY_VALUE = {
  text: 'same',
  long_text: 'same',
  url: 'same',
  email: 'same',
  phone_number: 'same',
  uuid: 'same',
  number: 'same',
  rating: 'same',
  boolean: 'same',
  duration: 'same',
  date: 'same',
  created_on: 'same',
  last_modified: 'same',
  created_by: 'same',
  last_modified_by: 'same',
  autonumber: 'same',
  formula: 'same',
  count: 'same',
  rollup: 'same',
  lookup: 'same',
  file: 'same',
  form_view_edit_row: 'same',
  single_select: 'option',
  multiple_select: 'options',
  link_row: 'links',
  multiple_collaborators: 'people',
  password: 'password',
} as const satisfies Record<FieldTypeName, string>

/** A stored reference, as the key its lookup table in the snapshot is keyed by. */
const refKey = (id: unknown): string =>
  typeof id === 'number' || typeof id === 'string' ? String(id) : ''

/** A value on one side of a change, in the shape a cell of that type holds. */
function hydrate(metadata: HistoryFieldMetadata, stored: unknown): unknown {
  const kind = HISTORY_VALUE[metadata.type as FieldTypeName]
  const ids = Array.isArray(stored) ? (stored as unknown[]) : []

  switch (kind) {
    case 'option':
      return metadata.select_options?.[refKey(stored)] ?? null
    case 'options':
      return ids.map((id) => metadata.select_options?.[refKey(id)] ?? { id: Number(id) })
    case 'links':
      return ids.map((id) => ({
        id: Number(id),
        value: metadata.linked_rows?.[refKey(id)]?.value ?? '',
      }))
    case 'people':
      return ids.map((id) => metadata.collaborators?.[refKey(id)] ?? { id: Number(id) })
    default:
      return stored
  }
}

/** One thing shown on one side of a change. */
export interface HistoryItem {
  /**
   * What makes two of these the same across the two sides.
   *
   * The *stored* value rather than the rendered text: two select options can
   * carry the same name, and a change from one to the other would otherwise
   * cancel itself out and show as no change at all.
   */
  key: string
  text: string
}

/** The key for one element of a list-valued cell. */
function itemKey(stored: unknown): string {
  if (typeof stored === 'string' || typeof stored === 'number') return String(stored)
  // A file, whose `name` is the stored name on the server and not the one
  // somebody typed -- so two attachments of the same document are one item and
  // renaming one is a change.
  const named = stored as { name?: unknown } | null
  return typeof named?.name === 'string' ? named.name : JSON.stringify(stored)
}

/** What one side of a change shows, empty when there was nothing there. */
export function historyItems(
  metadata: HistoryFieldMetadata,
  stored: unknown,
  context?: FieldContext,
): HistoryItem[] {
  const field = metadata as unknown as Field
  const render = (value: unknown): string =>
    behaviorOf(metadata.type as FieldTypeName).toHumanReadable(field, value, context)

  if (Array.isArray(stored)) {
    return (stored as unknown[]).map((element) => ({
      key: itemKey(element),
      // The element rendered as a list of one, which is how its own type reads
      // it. A select option renders as its name whether it is alone or one of
      // five, and this way nothing here knows which types those are.
      text: render(hydrate(metadata, [element])),
    }))
  }

  // Only the scalar side drops an empty rendering, and the two are not the same
  // question. A scalar that reads as nothing *was* nothing. An element that
  // reads as nothing is a thing that happened -- a collaborator with no name
  // against it, say -- and dropping it would make the change disappear rather
  // than render thinly. That it renders thinly is a fault the grid's own cells
  // share, and it belongs where names are rendered rather than here.
  //
  // Rendered rather than stored emptiness, at that: a number of zero and an
  // unticked checkbox both read as something and are both falsy, and upstream's
  // truthiness test drops them -- so a cell going from 0 to 5 shows only the 5.
  const text = render(hydrate(metadata, stored))
  return text === '' ? [] : [{ key: itemKey(stored), text }]
}

/** What happened to a password, which is all a history entry can say about one. */
export type PasswordChange = 'set' | 'changed' | 'cleared'

function passwordChange(before: unknown, after: unknown): PasswordChange {
  if (after !== true) return 'cleared'
  return before === true ? 'changed' : 'set'
}

/** What the server sends in place of a value redacted for the reader. */
const REDACTED_VALUE = '[REDACTED]'

/** One field an entry changed, ready to render. */
export type HistoryChange = { key: string; name: string } & (
  | { kind: 'items'; removed: HistoryItem[]; added: HistoryItem[] }
  | { kind: 'password'; change: PasswordChange }
  | { kind: 'redacted' }
)

/**
 * The fields an entry changed, in the order the table puts them.
 *
 * In *that* order rather than the one they arrive in, which is whatever order
 * the serializer happened to walk a dictionary in and can differ between two
 * entries about the same two fields.
 *
 * A change to a field that has since been deleted is dropped. There is nothing
 * to call it: the snapshot carries the field's type and formatting but never
 * its name, so the alternative is a nameless row of before and after. The same
 * goes for a type this build does not know, which cannot happen while the field
 * still exists and can if the entry predates a type being removed.
 */
export function historyChanges(
  entry: HistoryEntry,
  fields: readonly Field[],
  context?: FieldContext,
): HistoryChange[] {
  const changes: { at: number; change: HistoryChange }[] = []

  // The snapshots rather than the two sides. They are the entry's own list of
  // what it is about -- the backend narrows them to the fields that changed and
  // then writes the same set into `before` and `after` -- and reading a value
  // that has no snapshot behind it would leave nothing to render it with.
  for (const [key, metadata] of Object.entries(entry.fields_metadata)) {
    if (!(metadata.type in FIELD_BEHAVIOR)) continue

    const at = fields.findIndex((field) => field.id === metadata.id)
    if (at === -1) continue
    const name = fields[at]?.name ?? ''

    const before = entry.before[key]
    const after = entry.after[key]

    // Withheld on both sides, and said so rather than handed to the type's
    // reader, which would draw the placeholder as a broken date or number.
    if (before === REDACTED_VALUE || after === REDACTED_VALUE) {
      changes.push({ at, change: { key, name, kind: 'redacted' } })
      continue
    }

    if (HISTORY_VALUE[metadata.type as FieldTypeName] === 'password') {
      changes.push({
        at,
        change: { key, name, kind: 'password', change: passwordChange(before, after) },
      })
      continue
    }

    const removed = historyItems(metadata, before, context)
    const added = historyItems(metadata, after, context)
    // What both sides hold is not a change. It is how a list-valued cell reads:
    // adding one collaborator to five sends all six back, and showing the five
    // that stayed as removed and added again buries the one that moved.
    const addedKeys = new Set(added.map((item) => item.key))
    const removedKeys = new Set(removed.map((item) => item.key))
    changes.push({
      at,
      change: {
        key,
        name,
        kind: 'items',
        removed: removed.filter((item) => !addedKeys.has(item.key)),
        added: added.filter((item) => !removedKeys.has(item.key)),
      },
    })
  }

  return changes.sort((a, b) => a.at - b.at).map(({ change }) => change)
}

/**
 * Whether an entry is worth a line at all.
 *
 * An update whose every field has since been deleted has nothing left to say --
 * "somebody updated this row" and no indication of what. Creating, deleting and
 * restoring are worth recording whatever became of the fields involved, and an
 * undone or redone creation carries no values by design: the backend records
 * the command and not a diff.
 */
export function isWorthShowing(entry: HistoryEntry, changes: HistoryChange[]): boolean {
  return changes.length > 0 || entry.action_type !== 'update_rows'
}

/** An entry and what it changed, ready to render. */
export interface ShownEntry {
  entry: HistoryEntry
  changes: HistoryChange[]
}

/** Back-to-back entries by the same person doing the same thing, shown as one line. */
export interface HistoryGroup {
  key: number
  /** Newest first, never empty. */
  entries: [ShownEntry, ...ShownEntry[]]
}

export interface HistoryDay {
  heading: DayHeading
  groups: HistoryGroup[]
}

const sameKind = (a: HistoryEntry, b: HistoryEntry): boolean =>
  a.user.id === b.user.id &&
  a.action_type === b.action_type &&
  a.action_command_type === b.action_command_type &&
  (a.impersonator_sub ?? null) === (b.impersonator_sub ?? null)

/**
 * The entries under a heading for the day they happened on, with each run of
 * consecutive entries of the same kind folded into one group -- the way the
 * database's activity history folds one operation's rows into one line.
 *
 * Consecutive rather than sorted, so the newest-first order the endpoint answers
 * in, and the next page arrives in, is kept.
 */
export function historyDays(entries: readonly ShownEntry[], now: Date): HistoryDay[] {
  const days: HistoryDay[] = []

  for (const shown of entries) {
    const heading = dayHeading(shown.entry.timestamp, now)
    let day = days[days.length - 1]
    if (day?.heading.key !== heading.key) {
      day = { heading, groups: [] }
      days.push(day)
    }
    const group = day.groups[day.groups.length - 1]
    if (group !== undefined && sameKind(group.entries[0].entry, shown.entry)) {
      group.entries.push(shown)
    } else {
      day.groups.push({ key: shown.entry.id, entries: [shown] })
    }
  }

  return days
}

/** The names of the columns a group changed, each once, in the order first met. */
export function changedNames(entries: readonly ShownEntry[]): string[] {
  return [...new Set(entries.flatMap(({ changes }) => changes.map((c) => c.name)))]
}

/** The history of one row, newest first, a page at a time. */
export function rowHistoryQuery(tableId: number, rowId: number) {
  return infiniteQueryOptions({
    queryKey: qk.rowHistory(tableId, rowId),
    queryFn: ({ pageParam }): Promise<HistoryPage> =>
      unwrap(
        getApi().GET('/api/database/rows/table/{table_id}/{row_id}/history/', {
          params: {
            path: { table_id: tableId, row_id: rowId },
            query: { limit: HISTORY_PAGE_SIZE, offset: pageParam },
          },
        }),
      ) as Promise<HistoryPage>,
    initialPageParam: 0,
    getNextPageParam: (last, _pages, lastOffset) =>
      last.next === null ? undefined : lastOffset + HISTORY_PAGE_SIZE,
  })
}

export function useRowHistory(tableId: number, rowId: number) {
  return useInfiniteQuery(rowHistoryQuery(tableId, rowId))
}
